import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, IconButton } from '@mui/material';
import { RefObject, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getSelectedOrderId } from 'entities/Order/model/selectors/ordersSelectors';
import { fetchOrder } from 'entities/Order/model/services/fetchOrder/fetchOrder';
import { RemoveItemFromOrder } from 'features/RemoveItemFromOrder';
import { RemoveItemFromOrderRef } from 'features/RemoveItemFromOrder/ui/RemoveItemFromOrder/RemoveItemFromOrder';
import { ChangeCatalogItemCategoryButton } from 'features/changeCatalogItemCategory';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';

const ORDER_ITEM_REMOVE_BUTTON_DATA_TEST_CLASS = 'order-item-remove-button';

interface OrderGroupedItemsTableRowActionsProps {
    itemId: string;
    catalogItemId?: string;
    itemName: string;
    categoryId: string;
    removeItemRef?: RefObject<RemoveItemFromOrderRef>;
    canEditCatalog: boolean;
    canDelete: boolean;
    isOrderSubmissionLocked: boolean;
}

export const OrderGroupedItemsTableRowActions = ({
    itemId,
    catalogItemId,
    itemName,
    categoryId,
    removeItemRef,
    canEditCatalog,
    canDelete,
    isOrderSubmissionLocked,
}: OrderGroupedItemsTableRowActionsProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const orderId = useSelector(getSelectedOrderId);

    const onAfterCatalogCategoryChange = useCallback(async () => {
        if (orderId) {
            await dispatch(fetchOrder({ id: orderId }));
        }
    }, [dispatch, orderId]);

    return (
        <Box
            sx={{
                marginLeft: 'auto',
                marginTop: '-9px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
            }}
        >
            {canEditCatalog && catalogItemId && (
                <Box
                    className="order-item-change-category-hover"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        opacity: 0,
                        pointerEvents: 'none',
                        transition: 'opacity 0.15s ease',
                        '@media (hover: none)': {
                            opacity: 1,
                            pointerEvents: 'auto',
                        },
                    }}
                >
                    <ChangeCatalogItemCategoryButton
                        visualVariant="orderRow"
                        items={[
                            {
                                id: catalogItemId,
                                title: itemName,
                                categoryId,
                            },
                        ]}
                        onAfterCatalogCategoryChange={
                            onAfterCatalogCategoryChange
                        }
                        data-testclass="order-item-change-category-button"
                    />
                </Box>
            )}
            {canDelete && removeItemRef && (
                <RemoveItemFromOrder
                    ref={removeItemRef}
                    itemId={itemId}
                    showConfirmationDialog={false}
                    renderButton={(onClick) => (
                        <IconButton
                            disabled={isOrderSubmissionLocked}
                            aria-label={t('Remove item')}
                            title={t('Remove item')}
                            sx={{
                                color: 'rgba(0, 0, 0, 0.3)',
                                width: '32px',
                                height: '32px',
                                padding: '6px',
                                boxSizing: 'border-box',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                },
                                '&.Mui-disabled': {
                                    opacity: 0.8,
                                    color: 'rgba(0, 0, 0, 0.3)',
                                    cursor: 'not-allowed',
                                },
                            }}
                            data-testclass={
                                ORDER_ITEM_REMOVE_BUTTON_DATA_TEST_CLASS
                            }
                            onClick={onClick}
                        >
                            <DeleteOutlineIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                    )}
                />
            )}
        </Box>
    );
};
